import { useState, useEffect } from "react";
import schedulingManager from "../../models/scheduling";
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import HelpButton from '../../tours/HelpButton';
import useAutoTour from '../../tours/useAutoTour';
import useChatIntro from '../../tours/useChatIntro';
import { api } from '../../api/client';

// ── Design tokens ─────────────────────────────────────────────────────────────
const NAVY = '#0C2D48';
const GREEN = '#27ae60';
const RED = '#E96A6A';
const YELLOW = '#e4e69b';
const PURPLE = '#5352ed';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';

const LOCATION_LABELS = {
  'zoom': '🎥 Zoom',
  'google-meet': '📹 Google Meet',
  'teams': '👥 Microsoft Teams',
  'phone': '📞 Phone Call',
  'in-person': '📍 In Person',
  'custom': '🔗 Custom Link',
};
const EVENT_TYPE_LABELS = {
  'one-on-one': '1:1',
  'round-robin': 'Round Robin',
  'group': 'Group',
  'collective': 'Collective',
};
const STATUS_COLORS = {
  confirmed: GREEN, pending: '#f39c12', cancelled: RED, completed: '#888',
};
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const DURATION_OPTIONS = [15, 20, 30, 45, 60, 90, 120];
const COLOR_OPTIONS = [PURPLE, GREEN, '#e74c3c', '#f39c12', '#1abc9c', '#3498db', NAVY, '#e91e63', '#ff5722'];

const DAY_KEY_MAP = {
  monday: 'mon', tuesday: 'tue', wednesday: 'wed',
  thursday: 'thu', friday: 'fri', saturday: 'sat', sunday: 'sun',
};
const DAY_KEY_REVERSE = Object.fromEntries(Object.entries(DAY_KEY_MAP).map(([k, v]) => [v, k]));

function toServerWorkingHours(wh) {
  const out = {};
  for (const [day, val] of Object.entries(wh || {})) out[DAY_KEY_MAP[day] || day] = val;
  return out;
}
function toClientWorkingHours(wh) {
  const out = {};
  for (const [day, val] of Object.entries(wh || {})) out[DAY_KEY_REVERSE[day] || day] = val;
  return out;
}

function fmtTime(isoOrDate) {
  if (!isoOrDate) return '';
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function fmtDate(isoOrDate) {
  if (!isoOrDate) return '';
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtDateShort(isoOrDate) {
  if (!isoOrDate) return '';
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
function uid() { return Math.random().toString(36).slice(2, 9); }

// ── Small shared components ───────────────────────────────────────────────────
function Pill({ label, color = '#eee', textColor = '#333', style }) {
  return (
    <span style={{ background: color, color: textColor, borderRadius: 20, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700, ...style }}>
      {label}
    </span>
  );
}
function StatusBadge({ status }) {
  const labels = { confirmed: 'Confirmed', pending: 'Pending', cancelled: 'Cancelled', completed: 'Completed' };
  return <Pill label={labels[status] || status} color={STATUS_COLORS[status] || '#eee'} textColor={['confirmed', 'cancelled'].includes(status) ? '#fff' : '#333'} />;
}
function StatCard({ value, label, icon, color = NAVY }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '1.1rem 1.3rem', minWidth: 120, flex: 1 }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 2 }}>{icon} {label}</div>
    </div>
  );
}
function SectionHeader({ title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.2rem' }}>
      <h3 style={{ margin: 0, color: NAVY, fontSize: '1.05rem', fontWeight: 700 }}>{title}</h3>
      {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
    </div>
  );
}
function Btn({ onClick, children, variant = 'primary', style, disabled }) {
  const base = {
    padding: '0.45rem 1.1rem', border: 'none', borderRadius: 6, fontWeight: 600,
    fontSize: '0.88rem', cursor: disabled ? 'default' : 'pointer', fontFamily: 'Raleway, sans-serif',
    opacity: disabled ? 0.5 : 1, ...style,
  };
  const variants = {
    primary: { background: YELLOW, color: NAVY },
    danger: { background: RED, color: '#fff' },
    ghost: { background: 'transparent', color: '#666', border: `1px solid ${BORDER}` },
    navy: { background: NAVY, color: '#fff' },
    green: { background: GREEN, color: '#fff' },
  };
  return <button onClick={disabled ? undefined : onClick} style={{ ...base, ...variants[variant] }}>{children}</button>;
}
function Modal({ title, onClose, children, width = 520 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '1.2rem 1.4rem', borderBottom: `1px solid ${BORDER}` }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: NAVY }}>{title}</h3>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#888' }}>✕</button>
        </div>
        <div style={{ padding: '1.4rem' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
function DashboardTab({ stats, bookings, eventTypes, onBook, onNewET, isMobileLayout }) {
  const upcoming = bookings
    .filter(b => b.status !== 'cancelled' && new Date(b.scheduledStart) > new Date())
    .sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart))
    .slice(0, 5);

  return (
    <div>
      {/* Stats row */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.75rem' }}>
        <StatCard value={stats.activeEventTypes} label="Active Event Types" icon="📋" color={PURPLE} />
        <StatCard value={stats.thisWeek} label="Bookings This Week" icon="📅" color={GREEN} />
        <StatCard value={stats.upcoming} label="Upcoming" icon="⏰" color={NAVY} />
        <StatCard value={stats.openPolls} label="Open Polls" icon="🗳" color="#f39c12" />
      </div>

      {/* Quick actions */}
      <SectionHeader title="Quick Actions" />
      <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        {eventTypes.filter(e => e.isActive).slice(0, 4).map(et => (
          <button
            key={et.id}
            onClick={() => onBook(et.id)}
            style={{
              background: '#fff', border: `2px solid ${et.color}`,
              borderRadius: 8, padding: '0.6rem 1.1rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: 'Raleway, sans-serif', fontSize: '0.88rem', fontWeight: 600, color: '#222',
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: et.color, flexShrink: 0 }} />
            {et.name} <span style={{ color: '#aaa', fontWeight: 400 }}>{et.duration}m</span>
          </button>
        ))}
        <Btn onClick={onNewET} variant="ghost" style={{ fontSize: '0.85rem' }}>+ New Event Type</Btn>
      </div>

      {/* Upcoming bookings */}
      <SectionHeader title="Upcoming Bookings" />
      {upcoming.length === 0 ? (
        <p style={{ color: '#bbb', textAlign: 'center', padding: '2rem 0', fontSize: '0.9rem' }}>No upcoming bookings.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {upcoming.map(b => (
            <div key={b.id} style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 8, background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.7rem', color: '#888', border: `1px solid ${BORDER}`, lineHeight: 1.3 }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: NAVY }}>{new Date(b.scheduledStart).getDate()}</span>
                {new Date(b.scheduledStart).toLocaleString('default', { month: 'short' })}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#222' }}>{b.inviteeName || 'Guest'}</div>
                <div style={{ fontSize: '0.8rem', color: '#888' }}>{b.eventTypeName} · {fmtTime(b.scheduledStart)} – {fmtTime(b.scheduledEnd)}</div>
              </div>
              <StatusBadge status={b.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Availability Tab ───────────────────────────────────────────────────────────
function AvailabilityTab({ availability, onSave, isMobileLayout }) {
  const [form, setForm] = useState(availability);
  const [saved, setSaved] = useState(false);

  function handleDayToggle(day) {
    setForm(f => ({ ...f, workingHours: { ...f.workingHours, [day]: { ...f.workingHours[day], enabled: !f.workingHours[day].enabled } } }));
  }
  function handleDayTime(day, field, val) {
    setForm(f => ({ ...f, workingHours: { ...f.workingHours, [day]: { ...f.workingHours[day], [field]: val } } }));
  }
  function handleSave() { onSave(form); setSaved(true); setTimeout(() => setSaved(false), 2000); }

  const inp = { padding: '0.4rem 0.7rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem', fontFamily: 'Raleway, sans-serif', background: '#fff' };

  return (
    <div style={{ maxWidth: 600 }}>
      <SectionHeader title="Working Hours" />
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden', marginBottom: '1.5rem' }}>
        {Object.keys(DAY_LABELS).map((day, i) => {
          const dh = form.workingHours[day];
          return (
            <div key={day} style={{ display: 'flex', alignItems: isMobileLayout ? 'flex-start' : 'center', flexWrap: isMobileLayout ? 'wrap' : 'nowrap', gap: 12, padding: '0.75rem 1.1rem', borderTop: i > 0 ? `1px solid ${BORDER}` : 'none', background: dh.enabled ? '#fff' : BG }}>
              <input type="checkbox" checked={dh.enabled} onChange={() => handleDayToggle(day)} style={{ width: 16, height: 16, accentColor: GREEN, cursor: 'pointer' }} />
              <span style={{ width: 40, fontWeight: 600, fontSize: '0.88rem', color: dh.enabled ? '#222' : '#bbb' }}>{DAY_LABELS[day]}</span>
              {dh.enabled ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: isMobileLayout ? 'wrap' : 'nowrap', width: isMobileLayout ? '100%' : 'auto' }}>
                  <input type="time" value={dh.start} onChange={e => handleDayTime(day, 'start', e.target.value)} style={inp} />
                  <span style={{ color: '#bbb' }}>→</span>
                  <input type="time" value={dh.end} onChange={e => handleDayTime(day, 'end', e.target.value)} style={inp} />
                </div>
              ) : (
                <span style={{ fontSize: '0.82rem', color: '#bbb', fontStyle: 'italic' }}>Unavailable</span>
              )}
            </div>
          );
        })}
      </div>

      <SectionHeader title="Scheduling Rules" />
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '1.1rem 1.2rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        {[
          { key: 'bufferBefore', label: 'Buffer Before', unit: 'mins', hint: 'Time before each meeting' },
          { key: 'bufferAfter', label: 'Buffer After', unit: 'mins', hint: 'Time after each meeting' },
          { key: 'minNotice', label: 'Minimum Notice', unit: 'mins', hint: 'Minimum advance notice required' },
          { key: 'maxAdvance', label: 'Max Advance', unit: 'days', hint: 'How far in advance people can book' },
        ].map(({ key, label, unit, hint }) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>{label}</div>
              <div style={{ fontSize: '0.78rem', color: '#aaa' }}>{hint}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="number" min={0} step={unit === 'mins' ? 5 : 1}
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: Number(e.target.value) }))}
                style={{ ...inp, width: 70, textAlign: 'center' }}
              />
              <span style={{ fontSize: '0.8rem', color: '#888' }}>{unit}</span>
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>Timezone</div>
            <div style={{ fontSize: '0.78rem', color: '#aaa' }}>Your scheduling timezone</div>
          </div>
          <input
            value={form.timezone || ''}
            onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}
            style={{ ...inp, width: 200, fontSize: '0.82rem' }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <Btn onClick={handleSave} variant="navy">Save Availability</Btn>
        {saved && <span style={{ color: GREEN, fontSize: '0.85rem', fontWeight: 600 }}>✓ Saved</span>}
      </div>
    </div>
  );
}

// ── Event Type Form (modal) ────────────────────────────────────────────────────
function EventTypeForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    name: '', description: '', duration: 30, color: PURPLE,
    type: 'one-on-one', location: 'google-meet', locationDetails: '',
    requiresConfirmation: false, maxAttendees: 1,
    questions: [], reminderMinutes: [60, 1440],
    ...initial,
  });
  const [qText, setQText] = useState('');

  const inp = { padding: '0.45rem 0.8rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem', fontFamily: 'Raleway, sans-serif', background: '#fff', width: '100%', boxSizing: 'border-box' };
  const row = { marginBottom: '1rem' };
  const label = { display: 'block', fontWeight: 600, fontSize: '0.83rem', color: '#555', marginBottom: 4 };

  function addQuestion() {
    if (!qText.trim()) return;
    setForm(f => ({ ...f, questions: [...f.questions, { id: uid(), label: qText.trim(), type: 'text', required: false }] }));
    setQText('');
  }
  function removeQuestion(id) { setForm(f => ({ ...f, questions: f.questions.filter(q => q.id !== id) })); }

  return (
    <div>
      <div style={row}>
        <span style={label}>Name</span>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} placeholder="e.g. 30 Minute Meeting" />
      </div>
      <div style={row}>
        <span style={label}>Description</span>
        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inp, resize: 'vertical', minHeight: 60 }} placeholder="Brief description for invitees..." />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
        <div>
          <span style={label}>Duration</span>
          <select value={form.duration} onChange={e => setForm(f => ({ ...f, duration: Number(e.target.value) }))} style={inp}>
            {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} minutes</option>)}
          </select>
        </div>
        <div>
          <span style={label}>Event Type</span>
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inp}>
            {Object.entries(EVENT_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <span style={label}>Location</span>
          <select value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} style={inp}>
            {Object.entries(LOCATION_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <span style={label}>Max Attendees</span>
          <input type="number" min={1} value={form.maxAttendees} onChange={e => setForm(f => ({ ...f, maxAttendees: Number(e.target.value) }))} style={inp} />
        </div>
      </div>
      {form.location === 'custom' && (
        <div style={row}>
          <span style={label}>Custom Link / Address</span>
          <input value={form.locationDetails} onChange={e => setForm(f => ({ ...f, locationDetails: e.target.value }))} style={inp} placeholder="https://... or address" />
        </div>
      )}
      <div style={row}>
        <span style={label}>Colour</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {COLOR_OPTIONS.map(c => (
            <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))} style={{ width: 26, height: 26, borderRadius: '50%', background: c, border: form.color === c ? '3px solid #333' : '2px solid transparent', cursor: 'pointer' }} />
          ))}
        </div>
      </div>
      <div style={{ ...row, display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={form.requiresConfirmation} onChange={e => setForm(f => ({ ...f, requiresConfirmation: e.target.checked }))} style={{ width: 15, height: 15, accentColor: GREEN }} />
        <span style={{ fontSize: '0.88rem', color: '#444' }}>Require manual confirmation before booking is confirmed</span>
      </div>

      {/* Intake questions */}
      <div style={{ ...row, borderTop: `1px solid ${BORDER}`, paddingTop: '1rem' }}>
        <span style={{ ...label, marginBottom: 8 }}>Intake Questions</span>
        {form.questions.map((q, i) => (
          <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ flex: 1, fontSize: '0.85rem', color: '#444', background: BG, padding: '0.4rem 0.7rem', borderRadius: 5 }}>{i + 1}. {q.label}</span>
            <button onClick={() => removeQuestion(q.id)} style={{ background: 'none', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={qText} onChange={e => setQText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addQuestion()} style={{ ...inp, flex: 1 }} placeholder="Add a question for invitees..." />
          <Btn onClick={addQuestion} variant="ghost">Add</Btn>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${BORDER}`, paddingTop: '1rem' }}>
        <Btn onClick={onClose} variant="ghost">Cancel</Btn>
        <Btn onClick={() => { if (form.name.trim()) onSave(form); }} variant="navy" disabled={!form.name.trim()}>
          {initial?.id ? 'Save Changes' : 'Create Event Type'}
        </Btn>
      </div>
    </div>
  );
}

// ── Event Types Tab ────────────────────────────────────────────────────────────
function EventTypesTab({ eventTypes, onEdit, onDelete, onToggle, onNew, onBook, bookingUsername }) {
  const [copiedId, setCopiedId] = useState(null);
  function copyLink(et) {
    const url = schedulingManager.getBookingUrl(et.id, bookingUsername);
    navigator.clipboard?.writeText(url).catch(() => { });
    setCopiedId(et.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <div>
      <SectionHeader title="Event Types" action={<Btn onClick={onNew} variant="navy">+ New Event Type</Btn>} />
      {eventTypes.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem 0', color: '#bbb' }}>
          No event types yet. Create one to get started.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {eventTypes.map(et => (
          <div key={et.id} style={{
            background: '#fff', border: `1px solid ${BORDER}`, borderLeft: `4px solid ${et.isActive ? et.color : '#ddd'}`,
            borderRadius: 8, padding: '1rem 1.2rem', display: 'flex', alignItems: 'center', gap: 12,
            opacity: et.isActive ? 1 : 0.6,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: NAVY }}>{et.name}</span>
                <Pill label={`${et.duration}m`} color={et.color + '22'} textColor={et.color} />
                <Pill label={EVENT_TYPE_LABELS[et.type] || et.type} color="#f0f0f0" textColor="#666" />
                <Pill label={LOCATION_LABELS[et.location] || et.location} color="#f0f0f0" textColor="#666" />
              </div>
              {et.description && <div style={{ fontSize: '0.82rem', color: '#888', marginBottom: 4 }}>{et.description}</div>}
              <div style={{ fontSize: '0.78rem', color: '#bbb' }}>
                Slug: /{et.bookingSlug}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Btn onClick={() => onBook(et.id)} variant="ghost" style={{ fontSize: '0.8rem' }}>Book</Btn>
              <Btn onClick={() => copyLink(et)} variant="ghost" style={{ fontSize: '0.8rem' }}>
                {copiedId === et.id ? '✓ Copied' : '🔗 Copy Link'}
              </Btn>
              <Btn onClick={() => onEdit(et)} variant="ghost" style={{ fontSize: '0.8rem' }}>Edit</Btn>
              <Btn onClick={() => onToggle(et.id)} variant="ghost" style={{ fontSize: '0.8rem', color: et.isActive ? RED : GREEN }}>
                {et.isActive ? 'Disable' : 'Enable'}
              </Btn>
              <Btn onClick={() => onDelete(et.id)} variant="danger" style={{ fontSize: '0.8rem' }}>Delete</Btn>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Booking Flow ──────────────────────────────────────────────────────────────
function BookingFlow({ eventTypeId, eventTypes, onClose, onBooked, tasks = [], onCreateTask }) {
  const et = eventTypes.find(e => e.id === eventTypeId);
  const availability = schedulingManager.getAvailability();
  const maxAdvance = availability.maxAdvance || 30;

  const [step, setStep] = useState('calendar');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [slotsMap, setSlotsMap] = useState({});
  const [weekOffset, setWeekOffset] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', notes: '' });
  const [answers, setAnswers] = useState({});
  const [booking, setBooking] = useState(null);

  // Re-run when event type, advance window, or task count changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!et) return;
    setSlotsMap(schedulingManager.getAvailableSlotsRange(et.duration, maxAdvance, tasks));
  }, [et?.id, maxAdvance, tasks.length]);

  if (!et) return null;

  const today = new Date(new Date().toDateString());
  // Only surface days that actually have open slots
  const availableDays = Object.keys(slotsMap)
    .filter(d => new Date(d + 'T12:00:00') >= today && (slotsMap[d] || []).length > 0)
    .sort();
  const weekDays = availableDays.slice(weekOffset * 7, (weekOffset + 1) * 7);
  const canGoPrev = weekOffset > 0;
  const canGoNext = (weekOffset + 1) * 7 < availableDays.length;
  const slots = selectedDay ? (slotsMap[selectedDay] || []) : [];

  const weekStart = weekDays[0] ? new Date(weekDays[0] + 'T12:00:00') : null;
  const weekEnd = weekDays[weekDays.length - 1] ? new Date(weekDays[weekDays.length - 1] + 'T12:00:00') : null;
  const weekLabel = weekStart && weekEnd
    ? `${weekStart.toLocaleDateString('default', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('default', { month: 'short', day: 'numeric' })}`
    : '';

  function handleBook() {
    const newBooking = schedulingManager.createBooking({
      eventTypeId: et.id,
      inviteeName: form.name,
      inviteeEmail: form.email,
      scheduledStart: selectedSlot.start.toISOString(),
      scheduledEnd: selectedSlot.end.toISOString(),
      notes: form.notes,
      answers,
    });

    // Create a matching task in the main task system so it appears in calendar/schedule views
    if (onCreateTask) {
      const meetingType =
        et.location === 'zoom' ? 'Zoom' :
          et.location === 'google-meet' ? 'Google Meet' :
            et.location === 'teams' ? 'Teams' : '';
      const detailsParts = [`Invitee: ${form.name} (${form.email})`];
      if (form.notes) detailsParts.push(form.notes);
      const answersText = Object.entries(answers)
        .map(([, v]) => v).filter(Boolean).join('\n');
      if (answersText) detailsParts.push(answersText);

      onCreateTask({
        title: `${et.name} with ${form.name}`,
        datetime: selectedSlot.start,
        deadline: selectedSlot.end,
        details: detailsParts.join('\n'),
        priority: 'Medium',
        taskType: 'Meeting',
        reminderOffset: 60,
        completed: false,
        assignedTeam: newBooking.assignedTo ? [newBooking.assignedTo] : [],
        busy: true,
        meetingType,
        location: et.location,
      });
    }

    setBooking(newBooking);
    setStep('confirm');
    onBooked();
  }

  if (step === 'confirm') {
    return (
      <Modal title="Booking Confirmed!" onClose={onClose} width={460}>
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>✅</div>
          <h3 style={{ color: GREEN, margin: '0 0 8px' }}>You're booked!</h3>
          <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 1rem' }}>
            {et.name} with {form.name || 'you'}
          </p>
          <div style={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '0.9rem', marginBottom: '1rem', textAlign: 'left' }}>
            <div style={{ color: '#555', fontSize: '0.88rem', marginBottom: 4 }}>📅 {fmtDate(selectedSlot.start)}</div>
            <div style={{ color: '#555', fontSize: '0.88rem', marginBottom: 4 }}>⏰ {fmtTime(selectedSlot.start)} – {fmtTime(selectedSlot.end)}</div>
            <div style={{ color: '#555', fontSize: '0.88rem' }}>{LOCATION_LABELS[et.location] || et.location}</div>
          </div>
          {booking?.status === 'pending' && (
            <div style={{ background: '#fff8e1', border: '1px solid #ffcc02', borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: '0.82rem', color: '#9a7200', marginBottom: '1rem' }}>
              ⏳ This booking requires confirmation — you'll be notified when approved.
            </div>
          )}
          <Btn onClick={onClose} variant="navy">Done</Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Book: ${et.name}`} onClose={onClose} width={600}>
      {step === 'calendar' && (
        <div>
          <p style={{ color: '#666', fontSize: '0.88rem', marginTop: 0 }}>{et.description}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
            <span style={{ background: et.color + '22', color: et.color, borderRadius: 5, padding: '3px 9px', fontWeight: 700, fontSize: '0.8rem' }}>
              {et.duration} min
            </span>
            <span style={{ color: '#888', fontSize: '0.85rem' }}>{LOCATION_LABELS[et.location]}</span>
          </div>

          {/* Week navigation */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <button
              onClick={() => { setWeekOffset(w => w - 1); setSelectedDay(null); }}
              disabled={!canGoPrev}
              style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '4px 12px', cursor: canGoPrev ? 'pointer' : 'default', opacity: canGoPrev ? 1 : 0.35, fontFamily: 'Raleway, sans-serif', fontSize: '0.82rem', color: NAVY }}
            >← Prev</button>
            <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#555' }}>{weekLabel}</span>
            <button
              onClick={() => { setWeekOffset(w => w + 1); setSelectedDay(null); }}
              disabled={!canGoNext}
              style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '4px 12px', cursor: canGoNext ? 'pointer' : 'default', opacity: canGoNext ? 1 : 0.35, fontFamily: 'Raleway, sans-serif', fontSize: '0.82rem', color: NAVY }}
            >Next →</button>
          </div>

          {/* Day picker — only days with available slots */}
          {availableDays.length === 0 ? (
            <p style={{ color: '#bbb', fontSize: '0.88rem', padding: '1.5rem 0', textAlign: 'center' }}>
              No available slots in the next {maxAdvance} days.
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: '1.25rem' }}>
              {weekDays.map(d => {
                const slotCount = (slotsMap[d] || []).length;
                const date = new Date(d + 'T12:00:00');
                const isSelected = selectedDay === d;
                return (
                  <button
                    key={d}
                    onClick={() => setSelectedDay(isSelected ? null : d)}
                    style={{
                      padding: '8px 4px', borderRadius: 8,
                      border: isSelected ? `2px solid ${et.color}` : `1px solid ${BORDER}`,
                      background: isSelected ? et.color + '18' : '#fff',
                      cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
                      textAlign: 'center', transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ color: '#999', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>
                      {date.toLocaleDateString('default', { weekday: 'short' })}
                    </div>
                    <div style={{ color: isSelected ? et.color : NAVY, fontSize: '0.95rem', fontWeight: 700 }}>
                      {date.getDate()}
                    </div>
                    <div style={{ color: et.color, fontSize: '0.68rem', fontWeight: 600, marginTop: 2 }}>
                      {slotCount} slot{slotCount !== 1 ? 's' : ''}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Slot picker */}
          {selectedDay && (
            <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: NAVY, marginBottom: 10 }}>
                {fmtDate(new Date(selectedDay + 'T12:00:00'))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {slots.map((slot, i) => (
                  <button
                    key={i}
                    onClick={() => { setSelectedSlot(slot); setStep('form'); }}
                    style={{
                      padding: '0.5rem 1.1rem', borderRadius: 6, border: `1.5px solid ${et.color}`,
                      background: '#fff', color: et.color, fontWeight: 700, fontSize: '0.85rem',
                      cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
                    }}
                  >
                    {fmtTime(slot.start)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step === 'form' && (
        <div>
          <div style={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.2rem' }}>
            <div style={{ fontWeight: 600, fontSize: '0.88rem', color: NAVY }}>{fmtDate(selectedSlot.start)}</div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>{fmtTime(selectedSlot.start)} – {fmtTime(selectedSlot.end)} · {LOCATION_LABELS[et.location]}</div>
          </div>
          {[['name', 'Your Name', 'text', true], ['email', 'Email Address', 'email', true], ['notes', 'Notes (optional)', 'textarea', false]].map(([key, ph, type, req]) => (
            <div key={key} style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.83rem', color: '#555', marginBottom: 4 }}>
                {ph} {req && <span style={{ color: RED }}>*</span>}
              </label>
              {type === 'textarea' ? (
                <textarea
                  value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem 0.8rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem', resize: 'vertical', minHeight: 60, boxSizing: 'border-box', fontFamily: 'Raleway, sans-serif' }}
                  placeholder={ph}
                />
              ) : (
                <input
                  type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem 0.8rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem', boxSizing: 'border-box', fontFamily: 'Raleway, sans-serif' }}
                  placeholder={ph}
                  required={req}
                />
              )}
            </div>
          ))}
          {et.questions?.map(q => (
            <div key={q.id} style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.83rem', color: '#555', marginBottom: 4 }}>
                {q.label} {q.required && <span style={{ color: RED }}>*</span>}
              </label>
              <input
                value={answers[q.id] || ''}
                onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem 0.8rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem', boxSizing: 'border-box', fontFamily: 'Raleway, sans-serif' }}
              />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${BORDER}`, paddingTop: '1rem' }}>
            <Btn onClick={() => setStep('calendar')} variant="ghost">← Back</Btn>
            <Btn onClick={handleBook} variant="navy" disabled={!form.name.trim() || !form.email.trim()}>Confirm Booking</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Bookings Tab ──────────────────────────────────────────────────────────────
function BookingsTab({ bookings, onConfirm, onCancel, onDelete }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = bookings.filter(b => {
    if (filter !== 'all' && b.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (b.inviteeName || '').toLowerCase().includes(q) ||
        (b.inviteeEmail || '').toLowerCase().includes(q) ||
        (b.eventTypeName || '').toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => new Date(b.scheduledStart) - new Date(a.scheduledStart));

  return (
    <div>
      <SectionHeader title="All Bookings" />
      <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          style={{ flex: 1, minWidth: 160, padding: '0.45rem 0.8rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem', fontFamily: 'Raleway, sans-serif' }}
        />
        {['all', 'confirmed', 'pending', 'cancelled', 'completed'].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            padding: '0.4rem 0.9rem', borderRadius: 20, border: `1px solid ${filter === s ? NAVY : BORDER}`,
            background: filter === s ? NAVY : '#fff', color: filter === s ? '#fff' : '#555',
            fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
            textTransform: 'capitalize',
          }}>
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p style={{ color: '#bbb', textAlign: 'center', padding: '2rem 0', fontSize: '0.9rem' }}>No bookings found.</p>
      ) : (
        <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
          {filtered.map((b, i) => (
            <div key={b.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '0.85rem 1.1rem',
              borderTop: i > 0 ? `1px solid ${BORDER}` : 'none',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#222' }}>{b.inviteeName || 'Guest'}</span>
                  <StatusBadge status={b.status} />
                </div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 2 }}>
                  {b.eventTypeName} · {fmtDate(b.scheduledStart)}, {fmtTime(b.scheduledStart)} – {fmtTime(b.scheduledEnd)}
                </div>
                {b.inviteeEmail && <div style={{ fontSize: '0.78rem', color: '#bbb' }}>{b.inviteeEmail}</div>}
              </div>
              <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                {b.status === 'pending' && (
                  <Btn onClick={() => onConfirm(b.id)} variant="green" style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}>Confirm</Btn>
                )}
                {(b.status === 'pending' || b.status === 'confirmed') && (
                  <Btn onClick={() => onCancel(b.id)} variant="ghost" style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem', color: RED }}>Cancel</Btn>
                )}
                <Btn onClick={() => onDelete(b.id)} variant="ghost" style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem', color: '#bbb' }}>✕</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Meeting Polls Tab ─────────────────────────────────────────────────────────
function PollsTab({ polls, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [voterName, setVoterName] = useState('');
  const [voterEmail, setVoterEmail] = useState('');

  function handleCreatePoll(data) {
    schedulingManager.createPoll(data);
    onRefresh();
    setShowForm(false);
  }
  function handleVote(pollId, slotId) {
    if (!voterName.trim()) return;
    schedulingManager.votePoll(pollId, slotId, voterName, voterEmail);
    onRefresh();
  }
  function handleConfirm(pollId, slotId) {
    schedulingManager.confirmPollSlot(pollId, slotId);
    onRefresh();
  }
  function handleDelete(id) {
    schedulingManager.deletePoll(id);
    onRefresh();
  }

  return (
    <div>
      <SectionHeader title="Meeting Polls" action={
        <Btn onClick={() => setShowForm(true)} variant="navy">+ New Poll</Btn>
      } />

      {/* Voter identity row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#888', whiteSpace: 'nowrap' }}>Voting as:</span>
        <input value={voterName} onChange={e => setVoterName(e.target.value)} placeholder="Your name (required to vote)" style={{ flex: 1, minWidth: 0, padding: '0.35rem 0.7rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.82rem', fontFamily: 'Raleway, sans-serif', boxSizing: 'border-box' }} />
        <input value={voterEmail} onChange={e => setVoterEmail(e.target.value)} placeholder="Email (optional)" style={{ flex: 1, minWidth: 0, padding: '0.35rem 0.7rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.82rem', fontFamily: 'Raleway, sans-serif', boxSizing: 'border-box' }} />
      </div>

      {polls.length === 0 && !showForm && (
        <div style={{ textAlign: 'center', padding: '3rem 0', color: '#bbb', fontSize: '0.9rem' }}>
          No polls yet. Create one to find the best meeting time for your group.
        </div>
      )}

      {polls.map(poll => {
        const topSlot = [...poll.proposedSlots].sort((a, b) => b.votes.length - a.votes.length)[0];
        return (
          <div key={poll.id} style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '1.1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: '0.85rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: NAVY }}>{poll.title}</div>
                {poll.description && <div style={{ fontSize: '0.82rem', color: '#888', marginTop: 2 }}>{poll.description}</div>}
                <div style={{ fontSize: '0.78rem', color: '#bbb', marginTop: 2 }}>
                  {poll.duration}min · {poll.proposedSlots.length} time slots · {poll.participants.length} participants
                </div>
              </div>
              <div style={{ display: 'flex', gap: 5, flexShrink: 0, alignItems: 'center' }}>
                <Pill label={poll.status} color={poll.status === 'confirmed' ? GREEN : poll.status === 'open' ? '#e8f5e9' : '#eee'} textColor={poll.status === 'confirmed' ? '#fff' : '#555'} />
                {poll.status === 'open' && topSlot && (
                  <Btn onClick={() => handleConfirm(poll.id, topSlot.id)} variant="green" style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}>✓ Confirm Best</Btn>
                )}
                <Btn onClick={() => handleDelete(poll.id)} variant="ghost" style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem', color: '#bbb' }}>✕</Btn>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {poll.proposedSlots.map(slot => {
                const isConfirmed = poll.confirmedSlotId === slot.id;
                const hasVoted = slot.votes.includes(voterEmail || voterName);
                const pct = poll.proposedSlots.some(s => s.votes.length > 0)
                  ? Math.round((slot.votes.length / Math.max(...poll.proposedSlots.map(s => s.votes.length), 1)) * 100)
                  : 0;
                return (
                  <div key={slot.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: isConfirmed ? '#e8f5e9' : BG, borderRadius: 6, padding: '0.6rem 0.8rem', border: `1px solid ${isConfirmed ? GREEN : BORDER}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>{fmtDate(slot.start)} · {fmtTime(slot.start)} – {fmtTime(slot.end)}</div>
                      <div style={{ height: 4, background: '#e0e0e0', borderRadius: 2, marginTop: 4, position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', background: GREEN, borderRadius: 2, width: `${pct}%`, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#888', minWidth: 50, textAlign: 'right' }}>
                      {slot.votes.length} vote{slot.votes.length !== 1 ? 's' : ''}
                    </div>
                    {poll.status === 'open' && voterName.trim() && (
                      <button onClick={() => handleVote(poll.id, slot.id)} style={{
                        background: hasVoted ? GREEN : '#fff', color: hasVoted ? '#fff' : GREEN,
                        border: `1px solid ${GREEN}`, borderRadius: 5, padding: '3px 10px', cursor: 'pointer',
                        fontSize: '0.78rem', fontWeight: 600, fontFamily: 'Raleway, sans-serif',
                      }}>
                        {hasVoted ? '✓' : '+ Vote'}
                      </button>
                    )}
                    {isConfirmed && <span style={{ color: GREEN, fontWeight: 700, fontSize: '0.8rem' }}>✓ Set</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {showForm && <NewPollForm onSave={handleCreatePoll} onClose={() => setShowForm(false)} />}
    </div>
  );
}

// ── New Poll Form (modal) ─────────────────────────────────────────────────────
function NewPollForm({ onSave, onClose }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [dur, setDur] = useState(60);
  const [slots, setSlots] = useState([{ id: '1', date: '', start: '', end: '' }]);

  function addSlot() { setSlots(s => [...s, { id: String(s.length + 1), date: '', start: '', end: '' }]); }
  function removeSlot(id) { setSlots(s => s.filter(x => x.id !== id)); }
  function updateSlot(id, k, v) { setSlots(s => s.map(x => x.id === id ? { ...x, [k]: v } : x)); }

  function save() {
    const validSlots = slots.filter(s => s.date && s.start && s.end).map(s => ({
      id: s.id,
      start: new Date(`${s.date}T${s.start}`).toISOString(),
      end: new Date(`${s.date}T${s.end}`).toISOString(),
      votes: [],
    }));
    if (!title.trim() || validSlots.length === 0) return;
    onSave({ title, description: desc, duration: dur, proposedSlots: validSlots, participants: [] });
  }

  const inp = { padding: '0.45rem 0.8rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.85rem', fontFamily: 'Raleway, sans-serif', background: '#fff' };

  return (
    <Modal title="New Meeting Poll" onClose={onClose} width={520}>
      <div style={{ marginBottom: '0.85rem' }}>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Poll title (e.g. Q2 Planning Meeting)" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
      </div>
      <div style={{ marginBottom: '0.85rem' }}>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional description..." style={{ ...inp, width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 50 }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 600 }}>Duration:</span>
        <select value={dur} onChange={e => setDur(Number(e.target.value))} style={{ ...inp }}>
          {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} min</option>)}
        </select>
      </div>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#555', marginBottom: 8 }}>Proposed Time Slots</div>
      {slots.map(slot => (
        <div key={slot.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
          <input type="date" value={slot.date} onChange={e => updateSlot(slot.id, 'date', e.target.value)} style={{ ...inp, flex: '1 1 140px', minWidth: 0, boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flex: '1 1 140px', minWidth: 0 }}>
            <input type="time" value={slot.start} onChange={e => updateSlot(slot.id, 'start', e.target.value)} style={{ ...inp, flex: 1, minWidth: 0, boxSizing: 'border-box' }} />
            <span style={{ color: '#bbb', flexShrink: 0 }}>–</span>
            <input type="time" value={slot.end} onChange={e => updateSlot(slot.id, 'end', e.target.value)} style={{ ...inp, flex: 1, minWidth: 0, boxSizing: 'border-box' }} />
            {slots.length > 1 && <button onClick={() => removeSlot(slot.id)} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', flexShrink: 0 }}>✕</button>}
          </div>
        </div>
      ))}
      <Btn onClick={addSlot} variant="ghost" style={{ marginBottom: '1rem', fontSize: '0.82rem' }}>+ Add Time Slot</Btn>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${BORDER}`, paddingTop: '1rem' }}>
        <Btn onClick={onClose} variant="ghost">Cancel</Btn>
        <Btn onClick={save} variant="navy" disabled={!title.trim() || slots.every(s => !s.date)}>Create Poll</Btn>
      </div>
    </Modal>
  );
}

// ── Main SchedulingHub ─────────────────────────────────────────────────────────
export default function SchedulingHub({ tasks = [], onCreateTask }) {
  useAutoTour('scheduling');
  useChatIntro('scheduling');
  const { currentUser } = useCurrentUser();
  const [tab, setTab] = useState('dashboard');
  const isMobileLayout = typeof window !== 'undefined'
    && (window.innerWidth <= 1024 || window.matchMedia('(pointer: coarse)').matches);
  const [availability, setAvailability] = useState(schedulingManager.getAvailability());
  const [eventTypes, setEventTypes] = useState(schedulingManager.getEventTypes());
  const [bookings, setBookings] = useState(schedulingManager.getBookings());
  const [polls, setPolls] = useState(schedulingManager.getPolls());
  const [stats, setStats] = useState(schedulingManager.getStats());
  const [editingET, setEditingET] = useState(null);
  const [showETForm, setShowETForm] = useState(false);
  const [bookingFlow, setBookingFlow] = useState(null); // eventTypeId string
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    api('/api/scheduling/availability')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const serverWh = data?.availability?.workingHours;
        const hasServerData = serverWh && typeof serverWh === 'object'
          && Object.values(serverWh).some(d => d?.enabled);
        if (hasServerData) {
          const merged = { ...data.availability, workingHours: toClientWorkingHours(serverWh) };
          schedulingManager.saveAvailability(merged);
          setAvailability(schedulingManager.getAvailability());
        } else {
          const local = schedulingManager.getAvailability();
          if (local.workingHours && Object.values(local.workingHours).some(d => d?.enabled)) {
            api('/api/scheduling/availability', {
              method: 'PUT',
              body: JSON.stringify({ ...local, workingHours: toServerWorkingHours(local.workingHours) }),
            }).catch(() => {});
          }
        }
      })
      .catch(() => {});
  }, []);

  function refresh() {
    setAvailability(schedulingManager.getAvailability());
    setEventTypes(schedulingManager.getEventTypes());
    setBookings(schedulingManager.getBookings());
    setPolls(schedulingManager.getPolls());
    setStats(schedulingManager.getStats());
  }

  function handleSaveET(data) {
    if (editingET?.id) schedulingManager.updateEventType(editingET.id, data);
    else schedulingManager.createEventType(data);
    refresh();
    setShowETForm(false);
    setEditingET(null);
  }

  function handleDeleteET(id) {
    const et = eventTypes.find(e => e.id === id);
    setDeleteConfirm({ id, name: et?.name || 'this event type' });
  }

  function performDeleteET(id) {
    schedulingManager.deleteEventType(id);
    refresh();
  }

  const TABS = [
    { id: 'dashboard', label: '📊 Dashboard', mobileLabel: '📊 Dashboard' },
    { id: 'event-types', label: '📋 Event Types', mobileLabel: '📋 Event Types' },
    { id: 'availability', label: '🕐 Availability', mobileLabel: '🕐 Availability' },
    { id: 'bookings', label: '📅 Bookings', mobileLabel: '📅 Bookings' },
    { id: 'polls', label: '🗳 Polls', mobileLabel: '🗳 Polls' },
  ];

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobileLayout ? '0.9rem' : '2rem 1rem', fontFamily: 'Raleway, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: isMobileLayout ? '1rem' : '1.75rem' }}>
        <div>
          <h2 style={{ margin: 0, color: NAVY, fontSize: isMobileLayout ? '1.05rem' : '1.5rem', fontWeight: 800 }}>📅 Scheduling</h2>
          <p style={{ margin: '3px 0 0', color: '#888', fontSize: '0.85rem' }}>Manage availability, event types, and bookings</p>
        </div>
        <HelpButton tourId="scheduling" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.2rem', borderBottom: `1px solid ${BORDER}`, paddingBottom: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            data-tour-id={`scheduling-${t.id}`}
            onClick={() => setTab(t.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0.55rem 1rem', fontFamily: 'Raleway, sans-serif',
              fontSize: '0.88rem', fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? NAVY : '#888',
              borderBottom: `2px solid ${tab === t.id ? NAVY : 'transparent'}`,
              marginBottom: -1,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >{isMobileLayout ? t.mobileLabel : t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'dashboard' && (
        <DashboardTab
          stats={stats} bookings={bookings} eventTypes={eventTypes}
          onBook={id => setBookingFlow(id)}
          onNewET={() => { setEditingET(null); setShowETForm(true); }}
          isMobileLayout={isMobileLayout}
        />
      )}
      {tab === 'event-types' && (
        <EventTypesTab
          eventTypes={eventTypes}
          onEdit={et => { setEditingET(et); setShowETForm(true); }}
          onDelete={handleDeleteET}
          onToggle={id => { schedulingManager.toggleEventType(id); refresh(); }}
          onNew={() => { setEditingET(null); setShowETForm(true); }}
          onBook={id => setBookingFlow(id)}
          bookingUsername={currentUser?.bookingUsername || null}
        />
      )}
      {tab === 'availability' && (
        <AvailabilityTab
          availability={availability}
          onSave={data => {
            schedulingManager.saveAvailability(data);
            localStorage.setItem('aidey_sched_setup_done', '1');
            window.dispatchEvent(new Event('aidey:onboardingProgress'));
            api('/api/scheduling/availability', {
              method: 'PUT',
              body: JSON.stringify({ ...data, workingHours: toServerWorkingHours(data.workingHours) }),
            }).catch(() => {});
            refresh();
          }}
          isMobileLayout={isMobileLayout}
        />
      )}
      {tab === 'bookings' && (
        <BookingsTab
          bookings={bookings}
          onConfirm={id => { schedulingManager.updateBookingStatus(id, 'confirmed'); refresh(); }}
          onCancel={id => { schedulingManager.updateBookingStatus(id, 'cancelled'); refresh(); }}
          onDelete={id => { schedulingManager.deleteBooking(id); refresh(); }}
        />
      )}
      {tab === 'polls' && (
        <PollsTab polls={polls} onRefresh={refresh} />
      )}

      {/* Event type form modal */}
      {showETForm && (
        <Modal title={editingET ? `Edit: ${editingET.name}` : 'New Event Type'} onClose={() => { setShowETForm(false); setEditingET(null); }}>
          <EventTypeForm initial={editingET} onSave={handleSaveET} onClose={() => { setShowETForm(false); setEditingET(null); }} />
        </Modal>
      )}

      {/* Booking flow */}
      {bookingFlow && (
        <BookingFlow
          eventTypeId={bookingFlow}
          eventTypes={eventTypes}
          onClose={() => setBookingFlow(null)}
          onBooked={() => { refresh(); }}
          tasks={tasks}
          onCreateTask={onCreateTask}
        />
      )}

      <ConfirmDeleteModal
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => performDeleteET(deleteConfirm.id)}
        itemType="event type"
        itemName={deleteConfirm?.name || ''}
        warningText="This will permanently delete this event type and all its associated bookings."
      />
    </div>
  );
}
